
import React from 'react'
import { mdx } from '@mdx-js/react'

/* @jsxRuntime classic */
/* @jsx mdx */

import DocsBeta from 'src/components/docs/DocsBeta/index.tsx';

export const metadata = {
  toc: true,
  layout: 'guide',
  description: 'Learn how to verify account ownership identity using bank statement uploads.'
};
const makeShortcode = name => function MDXDefaultShortcode(props) {
      console.warn("Component " + name + " was not imported, exported, or provided by MDXProvider as global scope")
      return <div {...props}/>
    };
const Header = makeShortcode("Header");
const Callout = makeShortcode("Callout");
const layoutProps = {
  metadata
};
const MDXLayout = "wrapper"
export default function MDXContent({
  components,
  ...props
}) {
  return <MDXLayout {...layoutProps} {...props} components={components} mdxType="MDXLayout">

    <Header title='Identity Document Upload' subtitle='Learn about statement upload-based account ownership verification' mdxType="Header" />
    <h4 {...{
      "id": "overview"
    }}>{`Overview`}</h4>
    <p>{`In order to provide the user an additional way to verify their account ownership, Identity Document Upload can be used to upload a bank statement, verify that statement belongs to the account in question, and verify the ownership information on the statement. This feature is intended primarily for use with Items created via loginless Auth flows, such as Same-Day Micro-deposits, Instant Micro-deposits, or Database Auth.`}</p>
    <Callout mdxType="Callout">
      <p>{`Identity Document Upload is available as an add-on to Identity. For pricing details, and to request access to Identity Document Upload, contact your Plaid account manager or sales. `}</p>
    </Callout>
    <p>{`To detect potential document fraud or document tampering during the Identity Document Upload flow, you can use the optional Fraud Risk feature, which scans for over two dozen different fraud signals in categories such as visual evidence of tampering, suspicious metadata, inconsistent contents, and similarity to known fraudulent documents.`}</p>
    <h4 {...{
      "id": "implementation"
    }}>{`Implementation`}</h4>
    <p>{`To use Identity Document Upload, you will first create the Item with another product such as Auth or Transfer. Then, run Link in `}<a parentName="p" {...{
        "href": "/docs/link/update-mode"
      }}>{`update mode`}</a>{` with the following parameters (in addition to the required fields):`}</p>
    <ul>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`products`}</inlineCode>{` array set to `}<inlineCode parentName="li">{`["identity"]`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`The `}<inlineCode parentName="li">{`identity.account_ids`}</inlineCode>{` array should contain the `}<inlineCode parentName="li">{`account_id`}</inlineCode>{` of the account to verify. Currently, only one `}<inlineCode parentName="li">{`account_id`}</inlineCode>{` can be specified.`}</li>
      <li parentName="ul"><inlineCode parentName="li">{`identity.is_document_upload`}</inlineCode>{` should be set to `}<inlineCode parentName="li">{`true`}</inlineCode>{`.`}</li>
      <li parentName="ul">{`(Optional) to enable Fraud Risk, set `}<inlineCode parentName="li">{`identity.parsing_configs`}</inlineCode>{` to `}<inlineCode parentName="li">{`["ocr", "risk_signals"]`}</inlineCode></li>
    </ul>
    <pre><code parentName="pre" {...{
        "className": "language-bash",
        "metastring": "switcher=false #Example /link/token/create call for Identity Document Upload",
        "switcher": "false",
        "#Example": true,
        "/link/token/create": true,
        "call": true,
        "for": true,
        "Identity": true,
        "Document": true,
        "Upload": true
      }}>{`curl -X POST https://sandbox.plaid.com/link/token/create \\
-H 'Content-Type: application/json' \\
-d '{
  "client_id": <ClientQD>"\${PLAID_CLIENT_ID}"</ClientQD>,
  "secret": <SecretQD>"\${PLAID_SECRET}"</SecretQD>,
  "client_name": "Insert Client name here",
  "products": ["identity"],
  "access_token": "Insert access token here",
  "identity": 
    {
      "is_document_upload": true, 
      "account_ids": ["ZXEbW7Rkr9iv1qj8abebU1KDJlkexgSgrLAod"], 
      "parsing_configs": ["ocr", "risk_signals"]
    },
  "country_codes": ["US"],
  "language": "en",
  "user": {
    "client_user_id": "unique_user_id"
  }
}'
`}</code></pre>
    <p>{`During update mode, the end user will be prompted to upload a bank statement. After the statement has been uploaded and processed, Plaid will send an `}<inlineCode parentName="p">{`IDENTITY: DOCUMENT_UPDATE_AVAILABLE`}</inlineCode>{` webhook. A `}<inlineCode parentName="p">{`"document_status": "OCR_PROCESSING_COMPLETE"`}</inlineCode>{` field in the webhook body indicates that the statement was successfully parsed.`}</p>
    <p>{`If the parsing is successful, you can call `}<inlineCode parentName="p">{`/identity/documents/uploads/get`}</inlineCode>{`, which will return the identity data parsed from the document in the same format as `}<inlineCode parentName="p">{`/identity/get`}</inlineCode>{`. This endpoint will also return a `}<inlineCode parentName="p">{`documents`}</inlineCode>{` array -- for any given document in the array, if the `}<inlineCode parentName="p">{`metadata.is_account_number_match`}</inlineCode>{` field is `}<inlineCode parentName="p">{`true`}</inlineCode>{`, Plaid has verified that the account number on the document matches the account number known to Plaid. If it is `}<inlineCode parentName="p">{`false`}</inlineCode>{`, the document does not substantiate the end user's ownership of the account.  `}</p>
    <pre><code parentName="pre" {...{
        "className": "language-json",
        "metastring": "switcher=false #Documents array",
        "switcher": "false",
        "#Documents": true,
        "array": true
      }}>{`"documents": [
  {
      "document_id": "1d107b7f-91fe-44c8-b8e9-325494addf5d",
      "metadata": {
          "document_type": "BANK_STATEMENT",
          "is_account_number_match": true,
          "last_updated": "2024-01-29T08:06:46Z",
          "uploaded_at": "2024-01-29T08:06:46Z"
      },
      "risk_insights": {
          "risk_signals": [
              {
                  "has_fraud_risk": true,
                  "page_number": 0,
                  "signal_description": "Creation date and modification date do not match",
                  "type": "METADATA_DATES_OUTSIDE_WINDOW"
              },
              {
                  "has_fraud_risk": true,
                  "page_number": 0,
                  "signal_description": "Adobe Acrobat",
                  "type": "SOFTWARE_BLACKLIST"
              }
          ],
          "risk_summary": {
              "risk_score": 100
          }
      }
  }
],
`}</code></pre>
    <p>{`If Fraud Risk was enabled, the `}<inlineCode parentName="p">{`document`}</inlineCode>{` object will contain a `}<inlineCode parentName="p">{`risk_insights`}</inlineCode>{` object, including details about potential risks detected in the uploaded document. The `}<inlineCode parentName="p">{`risk_insights.risk_summary.risk_score`}</inlineCode>{` field will contain a score summarizing the risk of the document. If the score is 80 or higher, we recommend treating the account identity as unverified and potentially high risk and sending the user through a manual verification flow.`}</p>
    <h5 {...{
      "id": "testing-identity-document-upload"
    }}>{`Testing Identity Document Upload`}</h5>
    <p>{`In the Sandbox, Plaid will not parse the uploaded bank statement and will instead return pre-populated test user data. Because of this, Sandbox will proceed much faster than Production; you should make sure your app can handle the asynchronous behavior of Production, in which the user's statement will be verified after they have completed Link, rather than during the Link session. `}</p>
    <p>{`In Sandbox, you must provide a valid webhook URI in order to upload documents. `}</p>
    </MDXLayout>;
}
;
MDXContent.isMDXComponent = true;

            export const tableOfContents = [
      {
        id: "overview",
        level: 4,
        title: "Overview",
      },
{
        id: "implementation",
        level: 4,
        title: "Implementation",
      }
];
          
layoutProps.tableOfContents = tableOfContents;
MDXContent.layoutProps = layoutProps;

MDXContent.layout = function (props) { return <DocsBeta {...props} /> };
